import { useEffect, useRef, useMemo } from 'react';
import api from '../components/api/api';
import ReceiptTracker from '../services/chat/ReceiptTracker';
import { browserScheduler } from '../services/chatEvents';
import { retryDelay } from '../services/chat/protocol';

// Only concrete server messages are acknowledged. Legacy APKs do not send IDs.
export default function usePortalChatReceipts({ token, messages, active, containerRef, onRead }) {
  const session = useRef(null);
  const callback = useRef(onRead);
  callback.current = onRead;
  const state = useMemo(() => ({ token, tracker: new ReceiptTracker({
    scheduler: browserScheduler, retryDelay,
    sendReceipt: (ids, viewed) => api.post(`/client-portal/chat/${viewed ? 'read' : 'delivered'}`,
      { token, message_ids: ids }, { skipLoading: true }),
    onRead: () => callback.current?.(),
  }) }), [token]);
  useEffect(() => {
    session.current = state;
    state.tracker.start();
    return () => state.tracker.dispose();
  }, [state]);
  useEffect(() => {
    if (!token) return undefined;
    const outgoing = messages.filter(m => Number.isInteger(m.id) && m.id > 0 && m.direction === 'out');
    const acknowledge = (ids, viewed) => state.tracker.acknowledge(ids, viewed);
    state.tracker.recordSnapshot(outgoing);
    acknowledge(outgoing.map(m => m.id), false);
    let observer;
    let disposed = false;
    const watch = () => {
      observer?.disconnect();
      if (!active || document.visibilityState === 'hidden' || window.prokatProstAppPaused || !containerRef.current || !window.IntersectionObserver) return;
      const allowed = new Set(outgoing.filter(m => !state.tracker.hasRead(m.id)).map(m => m.id));
      observer = new IntersectionObserver(entries => {
        if (disposed || session.current !== state || document.visibilityState === 'hidden' || window.prokatProstAppPaused) return;
        const ids = entries.filter(e => e.isIntersecting && e.intersectionRatio >= 0.1)
          .map(e => Number(e.target.dataset.chatMessageId)).filter(id => allowed.has(id));
        acknowledge(ids, true);
      }, { threshold: 0.1 }); // Viewport plus all scroll-container clipping.
      containerRef.current.querySelectorAll('[data-chat-message-id]').forEach(el => observer.observe(el));
    };
    watch();
    document.addEventListener('visibilitychange', watch);
    return () => { disposed = true; observer?.disconnect(); document.removeEventListener('visibilitychange', watch); };
  }, [token, messages, active, containerRef, state]);
}
